import { useCashRegisterSessionsStore } from '#src/stores/cashRegisterSessions/cashRegisterSessionsStore'
import cashRegisterErrorParser from '#src/utils/cashRegisterErrorParser'
import { logService } from '@nsftx/seven-client-retail-utils'
import * as cashRegisterApiService from './cashRegisterApiService'
import type { GetSessionsByTenantParams } from '#src/interfaces/CashRegisterSession'

const LOG_PREFIX = '[CashRegisterService]'

const getSessionsByTenant = async (tenantUuid: string, params?: GetSessionsByTenantParams) => {
  try {
    const { setSessions } = useCashRegisterSessionsStore()

    const { data } = await cashRegisterApiService.getSessionsByTenant(tenantUuid, params)
    setSessions(data.data)

    return data.data
  } catch (err) {
    logService.error(`${LOG_PREFIX} Failed to get sessions by tenant.`, {
      ...cashRegisterErrorParser.parseUpstream(err),
      code: 'SGP_CR_GET_SESSIONS_BY_TENANT_ERROR',
      details: {
        tenantUuid
      }
    })
    throw err
  }
}

const getSessionByDevice = async (deviceUuid: string, sessionUuid: string) => {
  try {
    const { data } = await cashRegisterApiService.getSessionByDevice(deviceUuid, sessionUuid)

    return data
  } catch (err) {
    logService.error(`${LOG_PREFIX} Failed to get session by device.`, {
      ...cashRegisterErrorParser.parseUpstream(err),
      code: 'SGP_CR_GET_SESSION_BY_DEVICE_ERROR',
      details: {
        deviceUuid,
        sessionUuid
      }
    })
    throw err
  }
}

const openSessionByDevice = async (deviceUuid: string) => {
  try {
    const { setSessions, setActiveSessionBalance } = useCashRegisterSessionsStore()

    const { data } = await cashRegisterApiService.openSessionByDevice(deviceUuid)
    setSessions(data)
    setActiveSessionBalance(data.startingFloats)

    return data
  } catch (err) {
    logService.error(`${LOG_PREFIX} Failed to open session by device.`, {
      ...cashRegisterErrorParser.parseUpstream(err),
      code: 'SGP_CR_OPEN_SESSION_BY_DEVICE_ERROR',
      details: {
        deviceUuid
      }
    })
    throw err
  }
}

const closeSessionByDevice = async (deviceUuid: string, sessionUuid: string) => {
  try {
    const { setActiveSessionBalance } = useCashRegisterSessionsStore()

    const { data } = await cashRegisterApiService.closeSessionByDevice(deviceUuid, sessionUuid)
    setActiveSessionBalance([])

    return data
  } catch (err) {
    logService.error(`${LOG_PREFIX} Failed to close session by device.`, {
      ...cashRegisterErrorParser.parseUpstream(err),
      code: 'SGP_CR_CLOSE_SESSION_BY_DEVICE_ERROR',
      details: {
        deviceUuid,
        sessionUuid
      }
    })
    throw err
  }
}

const optInSessionByDevice = async (deviceUuid: string, sessionUuid: string) => {
  try {
    const { addSessionUser } = useCashRegisterSessionsStore()

    const { data } = await cashRegisterApiService.optInSessionByDevice(deviceUuid, sessionUuid)
    addSessionUser(sessionUuid, {
      userUuid: data.userUuid,
      userType: data.userType,
      joinedAt: data.joinedAt,
      transactionCount: data.transactionCount
    })

    return data
  } catch (err) {
    logService.error(`${LOG_PREFIX} Failed to opt in session by device.`, {
      ...cashRegisterErrorParser.parseUpstream(err),
      code: 'SGP_CR_OPT_IN_SESSION_BY_DEVICE_ERROR',
      details: {
        deviceUuid,
        sessionUuid
      }
    })
    throw err
  }
}

export {
  getSessionsByTenant,
  getSessionByDevice,
  openSessionByDevice,
  closeSessionByDevice,
  optInSessionByDevice
}
